/** Price label/amount resolution for property cards plus the visual-props memo comparator used by the mobile feed. */

import { formatCurrency } from '@federa/utils/formatters/currency-core';
import { getNestedValue } from '@federa/utils/data/nested';
import { PROPERTY_KEYS } from '@federa/const/property-keys';
import { LABEL_ASK, LABEL_AUCTION_OR_BEST_OFFER } from '@federa/const/label-values';
import type { PropertyPriceData, PropertyPriceDisplay } from '@federa/types/api-property';

// Price Display Logic

/** Shared price display for the no-bid auction and default fallthrough cases (single source, not copy-pasted). The
 *  PHRASE is read from `const/label-values.ts` rather than spelled here: the property page's ask row answers the same
 *  state with the same sentence, and until 2026-09-05 that was two homes for one copy decision. */
const AUCTION_OR_BEST_OFFER_DISPLAY: PropertyPriceDisplay = { label: null, amount: LABEL_AUCTION_OR_BEST_OFFER };

/** Price label + amount: an active auction with bids shows the bid, else the auction-or-best-offer label; a non-auction the ask. */
export function getPropertyPriceDisplay(data: PropertyPriceData): PropertyPriceDisplay {
	const { isAuction, remainingSeconds, highestBidAmount, listPrice } = data;

	const showAuctionAsk = remainingSeconds !== null && remainingSeconds > 0;
	const showListAsk = !isAuction && typeof listPrice === 'number' && listPrice > 0;

	if (showAuctionAsk) {
		if (highestBidAmount > 0) {
			return { label: 'Bid', amount: formatCurrency(highestBidAmount) };
		} else {
			return AUCTION_OR_BEST_OFFER_DISPLAY;
		}
	} else if (showListAsk) {
		return { label: LABEL_ASK, amount: formatCurrency(listPrice) };
	} else {
		// Default case - unified with auction case
		return AUCTION_OR_BEST_OFFER_DISPLAY;
	}
}

// Property Memo Comparison Utilities

/** Property paths that affect visual rendering - used for React memo comparison */
const VISUAL_RENDER_PATHS = [
	['id'],
	[PROPERTY_KEYS.HIGHEST_BID, 'amount'],
	// The feed-hub delta writes this SCALAR, not highestBid.amount -- the hub sends an amount and never invents a Bid
	// around it. Omitted here, a live bid updated state and the memo then reported nothing visual had changed, so the
	// card kept rendering "Auction or Best offer" while the event sat applied in the store.
	['highestBidAmount'],
	['askPrice'],
	['last'],
	['auctionEndUtc'],
	['matchingActiveTransaction', 'listPrice'],
] as const;

/** Equality across all visual render paths -- the memo comparison function for FeedProperty in PropertyFeed. */
export function areVisualPropsEqual<T extends Record<string, unknown>>(prev: T, next: T): boolean {
	for (const path of VISUAL_RENDER_PATHS) {
		const dotPath = path.join('.');
		if (getNestedValue(prev, dotPath) !== getNestedValue(next, dotPath)) {
			return false;
		}
	}
	return true;
}
